import { Badge, Button, Calendar, CalendarProps, Space, Tag } from "antd"
import Title from "antd/es/typography/Title"
import { useTranslation } from "react-i18next"
import { useAssignations, useAssignationsCalendar } from "./queries"
import { useEffect, useRef, useState } from "react"
import Loader from "components/loader"
import styles from "../styles.module.scss"
import { IAssignation } from "./types"
import OverviewCardWithDate from "components/overview-card-with-date"
import { IEventDetail } from "pages/events/types"

import { ReactComponent as DownloadIcon } from "assets/icons/download.svg"
import { ReactComponent as InfoIcon } from "assets/icons/info.svg"
import { ReactComponent as SubstituteIcon } from "assets/icons/subtitue.svg"
import { usePopup } from "utils/use-popup"
import EventDetailsModal from "../event-details-modal"
import SubstituteAssignationModal from "./substitution"
import PageHeader from "modules/layout/dashboard/main/page-header"
import dayjs from "dayjs"

const statusTagColors = (status: IEventDetail["status"]) => {
  switch (status) {
    case "draft":
    case "approved":
      return "var(--color-success)"
    case "execution":
      return "var(--color-primary)"
    case "terminated":
    case "closed":
      return "var(--color-accent2)"
    default:
      return "var(--color-error)"
  }
}

const Assignations = () => {
  const { t } = useTranslation()
  const [calendarValue, setCalendarValue] =
    useState<CalendarProps<any>["value"]>(dayjs)
  const [calendarMode, setCalendarMode] =
    useState<CalendarProps<any>["mode"]>("month")
  const listRef = useRef(null)
  let shouldScroll = useRef(false)
  const assignations = useAssignations()
  const assignationsCalendar = useAssignationsCalendar()

  const eventDetailsModal = usePopup<IEventDetail>()
  const substituteAssignationModal = usePopup<string>()

  const CellRender: CalendarProps<any>["cellRender"] = (current, info) => {
    let events: IAssignation[] = []
    if (info.type === "date") {
      events =
        assignations.data?.filter(
          (assignation) =>
            assignation.event.start_date === current.format("YYYY-MM-DD")
        ) ?? []
    }
    if (info.type === "month") {
      events =
        assignations.data?.filter(
          (assignation) =>
            parseInt(assignation.event.start_date.split("-")[1]) ===
            current.month() + 1
        ) ?? []
    }

    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "5px",
        }}
      >
        <Badge
          showZero
          count={events?.length}
          style={{
            backgroundColor:
              events?.length !== 0 ? "var(--color-primary)" : "transparent",
          }}
        />
      </div>
    )
  }

  const handleSelect: CalendarProps<any>["onSelect"] = (date, selectInfo) => {
    shouldScroll.current = true
  }

  const handlePanelChange: CalendarProps<any>["onPanelChange"] = (
    date,
    mode
  ) => {
    setCalendarMode(mode)
  }

  useEffect(() => {
    if (shouldScroll.current && listRef.current) {
      ;(listRef.current as any)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
      shouldScroll.current = false
    }
  })

  return (
    <>
      <PageHeader
        hasBackButton={false}
        title={<Title level={4}>{t("assignations.assignation_other")}</Title>}
        rightContent={
          <Button
            type="text"
            size="middle"
            shape="circle"
            loading={assignationsCalendar.isLoading}
            onClick={() => assignationsCalendar.mutate()}
            icon={<DownloadIcon height={14} />}
          />
        }
      />
      <div className={styles.calendarContainer}>
        {assignations.isLoading && (
          <div className={styles.calendarLoader}>
            <Loader />
          </div>
        )}
        <Calendar
          fullscreen={false}
          value={calendarValue}
          onChange={setCalendarValue}
          cellRender={CellRender}
          onPanelChange={handlePanelChange}
          mode={calendarMode}
          onSelect={handleSelect}
        />
      </div>
      <Space direction="vertical" ref={listRef}>
        {assignations.data
          ?.filter((el) =>
            calendarValue
              ? calendarMode === "month"
                ? el.event.start_date === calendarValue.format("YYYY-MM-DD")
                : parseInt(el.event.start_date.split("-")[1]) ===
                  calendarValue.month() + 1
              : false
          )
          .sort(
            (a, b) =>
              new Date(a.event.start_date).getTime() -
              new Date(b.event.start_date).getTime()
          )
          .map(({ id, has_substitution, event }) => (
            <OverviewCardWithDate
              key={id}
              date={event.start_date}
              actions={[
                <Tag
                  style={{
                    marginRight: 0,
                  }}
                  color={statusTagColors(event.status)}
                >
                  {event.displayed_status}
                </Tag>,
                <Button
                  style={{
                    width: "100%",
                  }}
                  icon={<InfoIcon height={20} />}
                  size="small"
                  type="text"
                  onClick={() => eventDetailsModal.open(event)}
                />,
                <Button
                  style={{
                    width: "100%",
                  }}
                  icon={
                    <Badge dot={has_substitution} color="var(--color-primary)">
                      <SubstituteIcon
                        height={20}
                        fill={
                          ["execution", "terminated", "closed"].includes(
                            event.status
                          ) ||
                          has_substitution ||
                          event.event_type === "entente"
                            ? "rgba(0,0,0,0.25)"
                            : "currentColor"
                        }
                      />
                    </Badge>
                  }
                  size="small"
                  type="text"
                  disabled={
                    ["execution", "terminated", "closed"].includes(
                      event.status
                    ) ||
                    has_substitution ||
                    event.event_type === "entente"
                  }
                  onClick={() => substituteAssignationModal.open(id)}
                />,
              ]}
              Content={
                <>
                  <p>{event.name}</p>
                  <p>
                    {event.start_hour}
                    {" - "}
                    {event.end_hour}
                  </p>
                  <p>{event.place?.name ?? event.activity_room?.name}</p>
                </>
              }
            />
          ))}
      </Space>
      <EventDetailsModal
        {...eventDetailsModal}
        open={eventDetailsModal.isOpen}
      />
      <SubstituteAssignationModal
        {...substituteAssignationModal}
        open={substituteAssignationModal.isOpen}
      />
    </>
  )
}

export default Assignations
